import { useState, useEffect, useMemo } from "react";
import { Search, UserPlus, CheckCircle, XCircle, Edit, Trash2, Mail, Phone, Loader2, AlertCircle, Download, Plus, Eye, Save, MapPin } from "lucide-react";
import * as XLSX from "xlsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { getAllUsers, createUser, updateUser, deleteUser, getSubscriptionPlans, getAccountCategories } from "@/services/api";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

export const Users = () => {
  const [users, setUsers] = useState([]);
  const [totalUsersCount, setTotalUsersCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [subscriptionFilter, setSubscriptionFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [dailyPickupFilter, setDailyPickupFilter] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const [locationUser, setLocationUser] = useState(null);
  const [locationInput, setLocationInput] = useState("");
  const [locationError, setLocationError] = useState("");
  const [isVerifyingLocation, setIsVerifyingLocation] = useState(false);
  const [togglingDailyPickup, setTogglingDailyPickup] = useState({});
  
  // Profile View State
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isProfileEditMode, setIsProfileEditMode] = useState(false);
  const [profileUser, setProfileUser] = useState(null);

  const checkIsActive = (user) => {
    if (user.subscription_status === 'active' && user.subscription_expiry) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expiryDate = new Date(user.subscription_expiry);
      expiryDate.setHours(0, 0, 0, 0);
      if (expiryDate.getTime() >= today.getTime()) {
        return true;
      }
    }
    return false;
  };

  const getSubscriptionStatusDisplay = (user) => {
    if (!user || user.subscription_status === "inactive" || !user.subscription_expiry) {
      return { text: "No active subscription", color: "text-muted-foreground" };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiryDate = new Date(user.subscription_expiry);
    expiryDate.setHours(0, 0, 0, 0);

    const timeDiff = expiryDate.getTime() - today.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (daysDiff < 0) {
      return { text: "Expired", color: "text-destructive font-semibold" };
    } else if (daysDiff === 0) {
      return { text: "Expires today", color: "text-amber-500 font-semibold" };
    } else {
      return { text: `Expires in ${daysDiff} days`, color: "text-success font-semibold" };
    }
  };

  const formatRegistrationDate = (user) => {
    const dateVal = user.created_at || user.createdAt;
    if (!dateVal) return "N/A";
    const timestamp = dateVal._seconds
      ? dateVal._seconds * 1000
      : dateVal.seconds
        ? dateVal.seconds * 1000
        : new Date(dateVal).getTime();
    if (Number.isNaN(timestamp)) return "N/A";
    return new Date(timestamp).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  };

  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [accountCategories, setAccountCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    contact: "",
    address: "",
    pincode: "",
    location_string: "",
    subscription_plan_id: "none",
    account_category: "Household",
    subscription_start_date: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch plans and categories first to support mapping
        let plans = [];
        try {
          const planRes = await getSubscriptionPlans();
          if (planRes.data && planRes.data.success) {
            plans = planRes.data.plans;
            setSubscriptionPlans(plans);
          }
        } catch (error) {
          console.error("Error fetching subscription plans:", error);
        }

        try {
          const catRes = await getAccountCategories();
          if (catRes.success) {
            setAccountCategories(catRes.categories);
          }
        } catch (error) {
          console.error("Error fetching account categories:", error);
        }

        // Fetch users and map subscription names
        // Fetch ALL users (paginated server-side) so phone/name/email search covers everyone
        const data = await getAllUsers('user');
        if (data && data.success) {
          const normalizedUsers = data.users
            .map(u => {
              const planId = u.subscription_plan || u.subscription_plan_id;
              let planName = u.subscription || "None";
              
              if (planId) {
                const plan = plans.find(p => p.id === planId || p.name === planId);
                if (plan) {
                  planName = plan.name;
                } else if (typeof planId === "string" && planId.trim() !== "" && planId !== "none") {
                  // Fallback to the raw string if it looks like a name
                  planName = planId;
                }
              }

              return {
                ...u,
                id: u.id || u._id,
                subscription: planName
              };
            })
            .sort((a, b) => {
              const getTimestamp = (obj) => {
                const dateVal = obj.created_at || obj.createdAt;
                if (!dateVal) return 0;
                if (dateVal._seconds) return dateVal._seconds * 1000;
                if (dateVal.seconds) return dateVal.seconds * 1000;
                return new Date(dateVal).getTime();
              };
              const dateA = getTimestamp(a);
              const dateB = getTimestamp(b);
              return dateB - dateA;
            });
          setUsers(normalizedUsers);
          setTotalUsersCount(data.totalCount || normalizedUsers.length);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const filteredUsers = useMemo(() => {
    const searchLower = searchQuery.toLowerCase();
    return users.filter(user => {
      const name = user.name || "";
      const email = user.email || "";
      const contact = user.phone || user.contact || "";
      const matchesSearch = name.toLowerCase().includes(searchLower) ||
                           email.toLowerCase().includes(searchLower) ||
                           contact.toLowerCase().includes(searchLower);
      const isActive = checkIsActive(user);
      const matchesStatus = statusFilter === "all" || 
                            (statusFilter === "active" && isActive) || 
                            (statusFilter === "inactive" && !isActive);
      let matchesSubscription = true;
      if (subscriptionFilter !== "all") {
        if (subscriptionFilter === "none") {
          matchesSubscription = !user.subscription || user.subscription === "None" || user.subscription === null;
        } else {
          matchesSubscription = user.subscription && user.subscription.toLowerCase().includes(subscriptionFilter.toLowerCase());
        }
      }
      const matchesSource = sourceFilter === "all" || 
                           (sourceFilter === "admin" && user.createdByAdmin) || 
                           (sourceFilter === "app" && !user.createdByAdmin);
      const matchesDailyPickup = dailyPickupFilter ? !!user.dailypickupuser : true;
      return matchesSearch && matchesStatus && matchesSubscription && matchesSource && matchesDailyPickup;
    });
  }, [users, searchQuery, statusFilter, subscriptionFilter, sourceFilter, dailyPickupFilter]);

  // Reset pagination when searching or filtering
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, subscriptionFilter, sourceFilter, dailyPickupFilter]);

  const isProfileFormDirty = useMemo(() => {
    if (!profileUser) return false;
    
    let isDirty = false;
    if (formData.name !== (profileUser.name || "")) isDirty = true;
    if (formData.email !== (profileUser.email || "")) isDirty = true;
    if (formData.contact !== (profileUser.phone || profileUser.contact || "")) isDirty = true;
    if (formData.address !== (profileUser.address || "")) isDirty = true;
    if (formData.pincode !== (profileUser.pincode || "")) isDirty = true;
    if (formData.location_string !== (profileUser.location_string || "")) isDirty = true;
    
    if (formData.account_category && formData.account_category !== (profileUser.account_category || "")) isDirty = true;
    if (formData.dailypickupuser !== (profileUser.dailypickupuser || false)) isDirty = true;

    const hasActiveSubscription = profileUser.subscription_plan && profileUser.subscription_plan !== "none" && profileUser.subscription_plan !== "";

    if (!hasActiveSubscription) {
      if (formData.subscription_plan_id !== "none" && formData.subscription_plan_id !== (profileUser.subscription_plan || "")) {
          isDirty = true;
      }
      
      if (formData.subscription_plan_id && formData.subscription_plan_id !== "none") {
         const originalStartDate = profileUser.subscription_start_date 
          ? new Date(profileUser.subscription_start_date).toISOString().split('T')[0] 
          : "";
          
         if (formData.subscription_start_date !== originalStartDate) {
             isDirty = true;
         }
      }
    }

    return isDirty;
  }, [formData, profileUser]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const paginatedUsers = filteredUsers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (loading) return <div>Loading...</div>;

  const handleToggleDailyPickup = async (user) => {
    const isCurrentlyDaily = !!user.dailypickupuser;
    const userId = user.id || user._id;
    
    setTogglingDailyPickup(prev => ({ ...prev, [userId]: true }));
    
    try {
      const result = await updateUser(userId, { dailypickupcustomer: !isCurrentlyDaily });
      if (result.success || (result && !result.error)) {
        toast.success(`Daily pickup ${!isCurrentlyDaily ? 'set' : 'removed'} successfully`);
        setUsers(users.map(u => 
          (u.id === userId || u._id === userId) 
            ? { ...u, dailypickupuser: !isCurrentlyDaily }
            : u
        ));
      } else {
        toast.error(result.detail || "Failed to update daily pickup status");
      }
    } catch (error) {
      console.error("Error updating daily pickup status:", error);
      toast.error(error.response?.data?.detail || "An error occurred while updating status");
    } finally {
      setTogglingDailyPickup(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user? This will remove their profile and authentication account.")) {
      return;
    }

    try {
      const result = await deleteUser(userId);
      if (result.success) {
        setUsers(users.filter(user => user.id !== userId));
        toast.success(result.message || "User deleted successfully");
      } else {
        toast.error(result.detail || "Failed to delete user");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "An error occurred while deleting user");
    }
  };

  const handleExport = () => {
    try {
      const exportData = filteredUsers.map(user => ({
        "Name": user.name || "N/A",
        "Email": user.email || "N/A",
        "Phone": user.phone || user.contact || "N/A",
        "Address": user.address || "N/A",
        "Pincode": user.pincode || "N/A",
        "Location String": user.location_string || "N/A",
        "Subscription Type": user.subscription || "None",
        "Status": checkIsActive(user) ? "Active" : "Inactive",
        "Source": user.createdByAdmin ? "Admin" : "App",
        "Pickups": user.pickups || user.stats?.pickups_completed || 0,
        "Created At": user.created_at ? (user.created_at._seconds ? new Date(user.created_at._seconds * 1000).toLocaleString() : new Date(user.created_at).toLocaleString()) : "N/A"
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Users");
      XLSX.writeFile(workbook, `Vruthi_Users_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success("Users data exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export users data");
    }
  };

  const handleProfileClick = (user) => {
    setProfileUser(user);
    setIsProfileEditMode(false);
    
    // Set form data for potential editing within profile view
    setFormData({
      name: user.name || "",
      email: user.email || "",
      contact: user.phone || user.contact || "",
      address: user.address || "",
      pincode: user.pincode || "",
      location_string: user.location_string || "",
      subscription_plan_id: user.subscription_plan || "",
      account_category: user.account_category || "",
      subscription_start_date: user.subscription_start_date 
        ? new Date(user.subscription_start_date).toISOString().split('T')[0] 
        : "",
      dailypickupuser: user.dailypickupuser || false
    });
    
    setIsProfileOpen(true);
  };

  const handleEditClick = (user) => {
    setProfileUser(user);
    setIsProfileEditMode(true);
    
    // Set form data for potential editing within profile view
    setFormData({
      name: user.name || "",
      email: user.email || "",
      contact: user.phone || user.contact || "",
      address: user.address || "",
      pincode: user.pincode || "",
      location_string: user.location_string || "",
      subscription_plan_id: user.subscription_plan || "",
      account_category: user.account_category || "",
      subscription_start_date: user.subscription_start_date 
        ? new Date(user.subscription_start_date).toISOString().split('T')[0] 
        : "",
      dailypickupuser: user.dailypickupuser || false
    });
    
    setIsProfileOpen(true);
  };

  const handleVerifyAndUpdateLocation = async () => {
    if (!locationInput.trim()) {
      setLocationError("Please enter a location.");
      return;
    }

    setLocationError("");
    setIsVerifyingLocation(true);

    try {
      const userId = locationUser.id || locationUser._id;
      
      // Call backend API to update the location string
      const result = await updateUser(userId, { location_string: locationInput });
      
      if (result.success) {
        toast.success("User location updated successfully.");
        
        // Assume backend might return updated user data or just use input
        const updatedLocationString = result.user?.location_string || locationInput;
        const updatedLocationCoords = result.user?.location || null;
        
        // Update local state
        setUsers(users.map(u => u.id === userId ? {
          ...u,
          location_string: updatedLocationString,
          location: updatedLocationCoords
        } : u));
        
        setIsLocationDialogOpen(false);
        setLocationInput("");
      } else {
        throw new Error(result.detail || "Failed to update user location.");
      }
      
    } catch (error) {
      console.error("Update Error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "An error occurred while updating location.";
      setLocationError(errorMsg);
    } finally {
      setIsVerifyingLocation(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    
    // Validation & Formatting
    const name = formData.name.trim();
    if (name.length < 3) {
      toast.error("Name must be at least 3 characters long");
      return;
    }

    let formattedContact = formData.contact.trim();

    if (formattedContact.length === 10 && /^\d+$/.test(formattedContact)) {
      formattedContact = `+91${formattedContact}`;
    } else if (!formattedContact.startsWith('+')) {
      if (!/^\d{10}$/.test(formattedContact.replace(/\D/g, ''))) {
          toast.error("Contact number must be 10 digits");
          return;
      }
      formattedContact = `+${formattedContact}`;
    }

    if (!/^\+\d{10,15}$/.test(formattedContact)) {
      toast.error("Invalid contact number format. Use 10 digits or international format (e.g., +91...)");
      return;
    }

    if (!formData.address.trim()) {
      toast.error("Address is required");
      return;
    }

    if (!/^\d{6}$/.test(formData.pincode.trim())) {
      toast.error("Pincode must be exactly 6 digits");
      return;
    }

    if (formData.subscription_plan_id && formData.subscription_plan_id !== "none" && !formData.subscription_start_date) {
      toast.error("Subscription start date is required when a plan is selected");
      return;
    }

    setCreateLoading(true);
    try {
      const payload = { ...formData, contact: formattedContact };
      
      if (!formData.subscription_plan_id || formData.subscription_plan_id === "none") {
        delete payload.subscription_start_date;
        delete payload.subscription_plan_id; // Optional: clean up "none" before sending
      }
      
      const result = await createUser(payload);
      
      if (result.success) {
        toast.success(result.message || "User created successfully");
        // Use the structure returned by backend for listing
        const selectedPlan = subscriptionPlans.find(p => p.id === formData.subscription_plan_id);
        
        const backendUser = result.user || {};
        const newUser = {
            ...backendUser,
            id: backendUser._id || backendUser.id,
            _id: backendUser._id || backendUser.id,
            name: backendUser.name || payload.name,
            email: backendUser.email || payload.email,
            phone: backendUser.contact || payload.contact,
            subscription: selectedPlan ? selectedPlan.name : "None",
            status: backendUser.status || "active", // Default for new users
            pickups: backendUser.pickups || backendUser.stats?.pickups_completed || 0,
            createdByAdmin: backendUser.createdByAdmin !== undefined ? backendUser.createdByAdmin : true,
            createdAt: backendUser.createdAt || { _seconds: Date.now() / 1000 }
        };
        
        setUsers([newUser, ...users]);
        setIsDialogOpen(false);
        setFormData({ name: "", email: "", contact: "", address: "", pincode: "", location_string: "", subscription_plan_id: "none", account_category: "Household", subscription_start_date: "" });
      } else {
        toast.error(result.detail || "Failed to create user");
      }
    } catch (error) {
      console.error("Error saving user:", error);
      toast.error(error.response?.data?.detail || "An error occurred while saving user");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleProfileSave = async () => {
    if (!profileUser) return;
    
    const userId = profileUser.id || profileUser._id;
    setCreateLoading(true);
    
    try {
      // Build dynamic payload with only changed fields
      const payload = {};
      
      if (formData.name !== (profileUser.name || "")) payload.name = formData.name;
      if (formData.email !== (profileUser.email || "")) payload.email = formData.email;
      if (formData.contact !== (profileUser.phone || profileUser.contact || "")) payload.contact = formData.contact;
      if (formData.address !== (profileUser.address || "")) payload.address = formData.address;
      if (formData.pincode !== (profileUser.pincode || "")) payload.pincode = formData.pincode;
      if (formData.location_string !== (profileUser.location_string || "")) payload.location_string = formData.location_string;
      
      if (formData.account_category && formData.account_category !== (profileUser.account_category || "")) {
        payload.account_category = formData.account_category;
      }

      // Check if user already has an active, locked subscription
      const hasActiveSubscription = profileUser.subscription_plan && profileUser.subscription_plan !== "none" && profileUser.subscription_plan !== "";

      if (!hasActiveSubscription) {
        if (formData.subscription_plan_id && formData.subscription_plan_id !== "none") {
          const isPlanChanged = formData.subscription_plan_id !== (profileUser.subscription_plan || "");
          
          const originalStartDate = profileUser.subscription_start_date 
            ? new Date(profileUser.subscription_start_date).toISOString().split('T')[0] 
            : "";
            
          const isDateChanged = formData.subscription_start_date !== originalStartDate;

          if (isPlanChanged || isDateChanged) {
            payload.subscription_plan_id = formData.subscription_plan_id;
            payload.subscription_start_date = formData.subscription_start_date;
          }
        } else if (formData.subscription_plan_id === "none" && profileUser.subscription_plan && profileUser.subscription_plan !== "none") {
          // User explicitly changed plan to "none" from an existing plan
          payload.subscription_plan_id = "none";
          // Do NOT include subscription_start_date
        }
      }

      if (formData.dailypickupuser !== (profileUser.dailypickupuser || false)) {
        payload.dailypickupcustomer = formData.dailypickupuser;
      }
      
      // Only validate start date if we are actively sending a new plan
      if (payload.subscription_plan_id && payload.subscription_plan_id !== "none" && !payload.subscription_start_date) {
        toast.error("Subscription start date is required when a plan is selected");
        setCreateLoading(false);
        return;
      }

      // If no changes, just return
      if (Object.keys(payload).length === 0) {
        setIsProfileEditMode(false);
        setCreateLoading(false);
        return;
      }

      const result = await updateUser(userId, payload);

      if (result.success && result.user) {
        toast.success("Profile updated successfully");
        const backendUser = result.user;
        const planId = backendUser.subscription_plan || backendUser.subscription_plan_id;
        const selectedPlan = subscriptionPlans.find(p => p.id === planId);
        
        const updatedUserRecord = {
          ...profileUser,
          ...backendUser,
          id: backendUser.id || backendUser._id || userId,
          subscription: selectedPlan ? selectedPlan.name : "None",
          dailypickupuser: backendUser.dailypickupcustomer !== undefined ? backendUser.dailypickupcustomer : (backendUser.dailypickupuser || false)
        };
        
        // Update local state list efficiently
        setUsers(prevUsers => prevUsers.map(u => (u.id === userId || u._id === userId) ? { ...u, ...updatedUserRecord } : u));
        
        // Update current profile user to reflect changes immediately
        setProfileUser(updatedUserRecord);
        
        setIsProfileEditMode(false);
      } else if (result.success) {
        // Fallback if backend does not return user object but indicates success
        toast.success("Profile updated successfully");
        const selectedPlan = subscriptionPlans.find(p => p.id === formData.subscription_plan_id);
        
        const updatedUserRecordFallback = {
          ...profileUser,
          ...payload,
          phone: payload.contact !== undefined ? payload.contact : (profileUser.phone || profileUser.contact),
          subscription: payload.subscription_plan_id !== undefined ? (selectedPlan ? selectedPlan.name : "None") : profileUser.subscription,
          subscription_plan: payload.subscription_plan_id !== undefined ? payload.subscription_plan_id : profileUser.subscription_plan,
          dailypickupuser: payload.dailypickupcustomer !== undefined ? payload.dailypickupcustomer : profileUser.dailypickupuser
        };
        
        delete updatedUserRecordFallback.contact; // cleaned up temp field
        delete updatedUserRecordFallback.dailypickupcustomer; // cleaned up temp field

        setUsers(prevUsers => prevUsers.map(u => (u.id === userId || u._id === userId) ? { ...u, ...updatedUserRecordFallback } : u));
        setProfileUser(updatedUserRecordFallback);
        setIsProfileEditMode(false);
      } else {
        toast.error(result.detail || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      const errorMsg = error.response?.data?.detail || error.message || "An error occurred while updating profile";
      toast.error(errorMsg);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">User Management</h1>
          <p className="text-muted-foreground mt-1">Manage and monitor all registered users</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleExport}
            variant="outline"
            className="shadow-sm"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button 
            onClick={() => {
              setFormData({ name: "", email: "", contact: "", address: "", pincode: "", location_string: "", subscription_plan_id: "none", account_category: "Household", subscription_start_date: "" });
              setIsDialogOpen(true);
            }}
            className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-md"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Add/Edit User Dialog */}
      {/* Add User Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        setIsDialogOpen(open);
        if (!open) {
          setFormData({ name: "", email: "", contact: "", address: "", pincode: "", location_string: "", subscription_plan_id: "none", account_category: "Household", subscription_start_date: "" });
        }
      }}>
      <DialogContent 
        className="sm:max-w-[425px] max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => {
          if (e.target.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if (e.target.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Enter the user details to create a new profile. Required fields are marked with *
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact">Contact Number *</Label>
                <Input
                  id="contact"
                  placeholder="9876543210"
                  required
                  value={formData.contact}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address *</Label>
              <Input
                id="address"
                placeholder="123 Main St, City"
                required
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pincode">Pincode *</Label>
                <Input
                  id="pincode"
                  placeholder="600001"
                  required
                  maxLength={6}
                  value={formData.pincode}
                  onChange={(e) => setFormData(prev => ({ ...prev, pincode: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location_string">Location Area / Code</Label>
                <Input
                  id="location_string"
                  placeholder="Plus Code or Coords"
                  value={formData.location_string}
                  onChange={(e) => setFormData(prev => ({ ...prev, location_string: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account_category">Account Category</Label>
              <Select 
                value={formData.account_category} 
                onValueChange={(val) => setFormData(prev => ({ ...prev, account_category: val }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Account Category" />
                </SelectTrigger>
                <SelectContent>
                  {accountCategories.map(cat => (
                    <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 border-t pt-4 mt-2">
              <Label className="text-sm font-semibold">Subscription Options (Optional)</Label>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="subscription_plan">Assign Plan</Label>
                <Select 
                  value={formData.subscription_plan_id} 
                  onValueChange={(val) => setFormData(prev => ({ ...prev, subscription_plan_id: val }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Subscription</SelectItem>
                    {subscriptionPlans.map((plan) => (
                      <SelectItem key={plan.id} value={plan.id}>
                        {plan.name} - ₹{plan.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="subscription_start_date">Start Date</Label>
                <Input
                  id="subscription_start_date"
                  type="date"
                  value={formData.subscription_start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, subscription_start_date: e.target.value }))}
                  disabled={formData.subscription_plan_id === "none"}
                />
              </div>
            </div>
            
            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createLoading}>
                {createLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  "Add User"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Profile Dialog */}
      <Dialog open={isProfileOpen} onOpenChange={(open) => {
        setIsProfileOpen(open);
        if (!open) setProfileUser(null);
      }}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto [&>button]:hidden">
          <DialogHeader className="flex flex-row items-center justify-between sticky top-0 bg-background z-10 pb-4 border-b">
            <div>
              <DialogTitle className="text-xl">User Profile</DialogTitle>
              <DialogDescription>
                {isProfileEditMode ? "Edit user details" : "Detailed view of the user's information"}
              </DialogDescription>
            </div>
            <div className="flex items-center space-x-2 bg-muted p-2 rounded-lg mr-6">
              <Label htmlFor="edit-mode-toggle" className="text-sm font-medium cursor-pointer">
                {isProfileEditMode ? "Edit Mode" : "View Mode"}
              </Label>
              <Switch
                id="edit-mode-toggle"
                checked={isProfileEditMode}
                onCheckedChange={setIsProfileEditMode}
              />
            </div>
          </DialogHeader>

          {profileUser && (
            <div className="space-y-6 py-4">
              {/* Profile Header */}
              <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg">
                <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center text-primary text-2xl font-bold uppercase">
                  {(profileUser.name || "U").charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-bold">{profileUser.name}</h3>
                  <div className="flex gap-2 mt-1">
                    <Badge variant={checkIsActive(profileUser) ? "success" : "destructive"}>
                      {checkIsActive(profileUser) ? "Active" : "Inactive"}
                    </Badge>
                    <Badge variant="outline">{profileUser.createdByAdmin ? "Admin Created" : "App User"}</Badge>
                    {profileUser.dailypickupuser && <Badge variant="secondary">Daily Pickup</Badge>}
                  </div>
                </div>
              </div>

              {/* Categorized Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* Personal Information */}
                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-muted-foreground uppercase tracking-wider border-b pb-1">Personal Info</h4>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Name</Label>
                      <Input 
                        value={isProfileEditMode ? formData.name : (profileUser.name || "")} 
                        onChange={(e) => setFormData({...formData, name: e.target.value})} 
                        className="h-8 text-sm disabled:opacity-100 disabled:bg-transparent" 
                        disabled={!isProfileEditMode}
                        placeholder="Name not provided"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Contact</Label>
                      <Input 
                        value={isProfileEditMode ? formData.contact : (profileUser.phone || profileUser.contact || "")} 
                        onChange={(e) => setFormData({...formData, contact: e.target.value})} 
                        className="h-8 text-sm disabled:opacity-100 disabled:bg-transparent" 
                        disabled={!isProfileEditMode}
                        placeholder="Contact not provided"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Email</Label>
                      <Input 
                        value={isProfileEditMode ? formData.email : (profileUser.email || "")} 
                        onChange={(e) => setFormData({...formData, email: e.target.value})} 
                        className="h-8 text-sm disabled:opacity-100 disabled:bg-transparent" 
                        disabled={!isProfileEditMode}
                        placeholder="Email not provided"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Account Category</Label>
                      <Select 
                        value={isProfileEditMode ? formData.account_category : (profileUser.account_category || "")} 
                        onValueChange={(val) => setFormData({...formData, account_category: val})}
                        disabled={!isProfileEditMode}
                      >
                        <SelectTrigger className="h-8 text-sm disabled:opacity-100 disabled:bg-transparent">
                          <SelectValue placeholder="Select Account Category" />
                        </SelectTrigger>
                        <SelectContent>
                          {accountCategories.map(cat => <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Address Details */}
                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-muted-foreground uppercase tracking-wider border-b pb-1">Address Details</h4>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Address</Label>
                      <Input 
                        value={isProfileEditMode ? formData.address : (profileUser.address || "")} 
                        onChange={(e) => setFormData({...formData, address: e.target.value})} 
                        className="h-8 text-sm disabled:opacity-100 disabled:bg-transparent" 
                        disabled={!isProfileEditMode}
                        placeholder="Address not provided"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Pincode</Label>
                      <Input 
                        value={isProfileEditMode ? formData.pincode : (profileUser.pincode || "")} 
                        onChange={(e) => setFormData({...formData, pincode: e.target.value})} 
                        className="h-8 text-sm disabled:opacity-100 disabled:bg-transparent" 
                        disabled={!isProfileEditMode}
                        placeholder="Pincode not provided"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Location Area / Code</Label>
                      <Input 
                        value={isProfileEditMode ? formData.location_string : (profileUser.sublocality || profileUser.location_string || "")} 
                        onChange={(e) => setFormData({...formData, location_string: e.target.value})} 
                        className="h-8 text-sm disabled:opacity-100 disabled:bg-transparent" 
                        placeholder="Location Code not provided"
                        disabled={!isProfileEditMode}
                      />
                    </div>
                    {/* Display actual lat/lng if available */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Coordinates</Label>
                      {profileUser.location && profileUser.location.lat && profileUser.location.lng ? (
                        <p className="text-sm font-medium text-success h-8 flex items-center">
                          {profileUser.location.lat.toFixed(4)}, {profileUser.location.lng.toFixed(4)}
                        </p>
                      ) : (
                        <p className="text-sm font-medium text-muted-foreground italic h-8 flex items-center">
                          Location not set
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Subscription & Pickup */}
                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-muted-foreground uppercase tracking-wider border-b pb-1">Subscription & Services</h4>
                  <div className="space-y-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Subscription Plan</Label>
                      <Select 
                        value={isProfileEditMode ? formData.subscription_plan_id : (profileUser.subscription_plan || "none")} 
                        onValueChange={(val) => setFormData({...formData, subscription_plan_id: val})}
                        disabled={!isProfileEditMode || (profileUser.subscription_plan && profileUser.subscription_plan !== "none" && profileUser.subscription_plan !== "")}
                      >
                        <SelectTrigger className="h-8 text-sm disabled:opacity-100 disabled:bg-transparent">
                          <SelectValue placeholder="No Subscription" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Subscription</SelectItem>
                          {subscriptionPlans.map(plan => <SelectItem key={plan.id} value={plan.id}>{plan.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        {isProfileEditMode ? "Subscription Start Date" : "Subscription Status"}
                      </Label>
                      {isProfileEditMode ? (
                        <Input 
                          type="date" 
                          value={formData.subscription_start_date} 
                          onChange={(e) => setFormData({...formData, subscription_start_date: e.target.value})} 
                          className="h-8 text-sm"
                          disabled={formData.subscription_plan_id === "none" || (profileUser.subscription_plan && profileUser.subscription_plan !== "none" && profileUser.subscription_plan !== "")}
                        />
                      ) : (
                        <p className={`text-sm ${getSubscriptionStatusDisplay(profileUser).color} h-8 flex items-center`}>
                          {getSubscriptionStatusDisplay(profileUser).text}
                        </p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Daily Pickup Service</Label>
                      <div className="flex items-center space-x-2 mt-1 h-8">
                        <Switch 
                          checked={isProfileEditMode ? formData.dailypickupuser : !!profileUser.dailypickupuser} 
                          onCheckedChange={(val) => setFormData({...formData, dailypickupuser: val})} 
                          disabled={!isProfileEditMode}
                        />
                        <span className="text-sm">{formData.dailypickupuser ? "Enabled" : "Disabled"}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Impact Stats (Always Read-Only) */}
                <div className="space-y-3">
                  <h4 className="text-sm font-bold text-muted-foreground uppercase tracking-wider border-b pb-1">Impact Stats</h4>
                  <div className="grid grid-cols-2 gap-2 bg-muted/20 p-3 rounded-lg border">
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase">Pickups</Label>
                      <p className="font-bold">{profileUser.pickups || profileUser.stats?.pickups_completed || 0}</p>
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase">CO2 Saved (kg)</Label>
                      <p className="font-bold">{profileUser.stats?.co2_converted || 0}</p>
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase">Trees Saved</Label>
                      <p className="font-bold">{profileUser.stats?.trees_saved || 0}</p>
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase">Animals Saved</Label>
                      <p className="font-bold">{profileUser.stats?.animals_saved || 0}</p>
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase">Eco Points</Label>
                      <p className="font-bold text-success">{profileUser.eco_points || 0}</p>
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase">Coins Balance</Label>
                      <p className="font-bold text-amber-500">{profileUser.coins_balance || 0}</p>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}

          <DialogFooter className="sticky bottom-0 bg-background pt-4 border-t">
            {isProfileEditMode ? (
              <div className="flex w-full justify-between">
                <Button variant="outline" onClick={() => setIsProfileEditMode(false)}>
                  Cancel Edit
                </Button>
                <Button onClick={handleProfileSave} disabled={createLoading || !isProfileFormDirty}>
                  {createLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Changes</>}
                </Button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setIsProfileOpen(false)}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exact Location Dialog */}
      <Dialog open={isLocationDialogOpen} onOpenChange={setIsLocationDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Exact Location</DialogTitle>
            <DialogDescription>
              Enter a Plus Code, latitude & longitude, or exact address for {locationUser?.name}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="exactLocation">Location Input</Label>
              <Input
                id="exactLocation"
                placeholder="e.g. 12.9716, 77.5946 or 7J7PXV3Q+..."
                value={locationInput}
                onChange={(e) => {
                  setLocationInput(e.target.value);
                  setLocationError("");
                }}
              />
              <p className="text-[10px] text-muted-foreground">
                Paste the coordinates or location URL received via WhatsApp.
              </p>
            </div>
            
            {locationError && (
              <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <p>{locationError}</p>
              </div>
            )}
            
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLocationDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleVerifyAndUpdateLocation} 
              disabled={isVerifyingLocation}
            >
              {isVerifyingLocation ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                "Save Location"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card 
          className="border-border cursor-pointer hover:bg-muted/50 transition-colors"
          onClick={() => {
            setStatusFilter("all");
            setSubscriptionFilter("all");
            setSourceFilter("all");
            setDailyPickupFilter(false);
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Users</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">{totalUsersCount}</h3>
              </div>
              <CheckCircle className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-border cursor-pointer hover:bg-success/10 transition-colors ${statusFilter === "active" ? "ring-2 ring-success bg-success/5" : ""}`}
          onClick={() => {
            setStatusFilter(prev => prev === "active" ? "all" : "active");
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Users</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">
                  {users.filter(u => checkIsActive(u)).length}
                </h3>
              </div>
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-border cursor-pointer hover:bg-secondary/10 transition-colors ${subscriptionFilter === "Premium" ? "ring-2 ring-secondary bg-secondary/5" : ""}`}
          onClick={() => {
            setSubscriptionFilter(prev => prev === "Premium" ? "all" : "Premium");
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Premium Users</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">
                  {users.filter(u => u.subscription && u.subscription.toLowerCase().includes("premium")).length}
                </h3>
              </div>
              <CheckCircle className="w-8 h-8 text-secondary" />
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`border-border cursor-pointer hover:bg-destructive/10 transition-colors ${statusFilter === "inactive" ? "ring-2 ring-destructive bg-destructive/5" : ""}`}
          onClick={() => {
            setStatusFilter(prev => prev === "inactive" ? "all" : "inactive");
          }}
        >
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Inactive Users</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">
                  {users.filter(u => !checkIsActive(u)).length}
                </h3>
              </div>
              <XCircle className="w-8 h-8 text-destructive" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle>Users List</CardTitle>
          <CardDescription>Search, filter and manage user accounts</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Subscription Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active Subscription</SelectItem>
                <SelectItem value="inactive">Inactive / Expired</SelectItem>
              </SelectContent>
            </Select>
            <Select value={subscriptionFilter} onValueChange={setSubscriptionFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by subscription" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                {subscriptionPlans.map((plan) => (
                  <SelectItem key={plan.id} value={plan.name}>
                    {plan.name}
                  </SelectItem>
                ))}
                <SelectItem value="none">No Subscription</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="admin">Admin Panel</SelectItem>
                <SelectItem value="app">Mobile App</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex items-center space-x-2 border rounded-md px-3 py-2 sm:h-10">
              <Checkbox 
                id="dailyPickupFilter" 
                checked={dailyPickupFilter}
                onCheckedChange={setDailyPickupFilter}
              />
              <Label htmlFor="dailyPickupFilter" className="text-sm cursor-pointer whitespace-nowrap">Daily Pickup</Label>
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead className="text-center">Location</TableHead>
                  <TableHead className="text-center">Daily Pickup</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Subscription</TableHead>
                  <TableHead className="hidden lg:table-cell">Pickups</TableHead>
                  <TableHead>Registered</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedUsers.map((user) => (
                  <TableRow key={user.id} className="hover:bg-muted/50 transition-smooth">
                    <TableCell>
                      <div className="space-y-1">
                        <p className="font-medium text-foreground">{user.name}</p>
                        <div className="flex flex-col gap-1 mt-1">
                          <div className="flex items-center gap-2 text-sm">
                            <Mail className="w-3 h-3 text-muted-foreground" />
                            <span className="text-muted-foreground">{user.email || "N/A"}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <Phone className="w-3 h-3 text-muted-foreground" />
                            <span className="text-muted-foreground">{user.phone || user.contact || "N/A"}</span>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      {user.location_string || user.sublocality || user.address ? (
                        <div className="flex items-center justify-center gap-1.5">
                          {user.location && (
                            <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />
                          )}
                          <MapPin className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                          <span
                            className="text-sm text-muted-foreground line-clamp-1 max-w-[200px]"
                            title={user.location_string || user.sublocality || user.address}
                          >
                            {user.location_string || user.sublocality || user.address}
                          </span>
                        </div>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 mx-auto"
                          onClick={() => {
                            setLocationUser(user);
                            setLocationInput(user.location_string || "");
                            setLocationError("");
                            setIsLocationDialogOpen(true);
                          }}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant={user.dailypickupuser ? "destructive" : "outline"}
                        size="sm"
                        onClick={() => handleToggleDailyPickup(user)}
                        disabled={togglingDailyPickup[user.id || user._id]}
                      >
                        {togglingDailyPickup[user.id || user._id] ? (
                          <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {user.dailypickupuser ? "Removing..." : "Adding..."}</>
                        ) : (
                          user.dailypickupuser ? "Remove" : "Add"
                        )}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {user.createdByAdmin ? "Admin" : "App"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.subscription && user.subscription.toLowerCase().includes("premium") ? "default" : "secondary"} className="font-medium">
                        {user.subscription}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-sm font-medium">{user.pickups || user.stats?.pickups_completed || 0}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatRegistrationDate(user)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => handleProfileClick(user)}
                          title="View Profile"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-muted-foreground hover:text-foreground"
                          onClick={() => handleEditClick(user)}
                          title="Quick Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteUser(user.id)}
                          title="Delete User"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          
          {totalPages > 1 && (
            <div className="mt-4">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                  <PaginationItem>
                    <span className="text-sm text-muted-foreground px-4">
                      Page {currentPage} of {totalPages}
                    </span>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Users;
